import { useState, type FormEvent } from "react";
import { Link, useParams } from "react-router";
import { adminApi } from "../../../lib/api";
import { consoleApi, type CommissionBase, type ConsoleCommissionRule, type ConsoleVenueDetail } from "../../../lib/admin-console-api";
import { useAuth } from "../../../lib/auth";
import { useAppPreferences } from "../../../lib/preferences";
import { ActivityTimeline, baseLabel, NotesPanel } from "../activity";
import { errorMessage, Facts, LoadState, PageHeader, Section, StatusChip, useFormatters, useLoad, useNotices, useReasonPrompt } from "../ui";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export function VenueRecordPage() {
  const { venueId = "" } = useParams();
  const { t } = useAppPreferences();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { dateOnly, dateTime, money } = useFormatters();
  const { banners, setError, setNotice } = useNotices();
  const { ask, dialog } = useReasonPrompt();
  const venue = useLoad(() => consoleApi.getVenue(venueId), [venueId]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const detail = venue.data;

  const run = async (work: () => Promise<unknown>, success: string, failure: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await work();
      setNotice(success);
      await venue.reload();
    } catch (error) {
      setError(errorMessage(error, failure));
    } finally {
      setBusy(false);
    }
  };

  const decide = (decision: "approve" | "reject") =>
    run(
      async () => {
        await adminApi.updateVenueStatus(venueId, { decision, review_note: note.trim() || null });
        setNote("");
      },
      t("Decision saved."),
      t("The decision was not saved. Your note is kept; try again."),
    );

  const takeDown = async (target: ConsoleVenueDetail) => {
    const reason = await ask({
      title: t("Take {name} off gaamed?", { name: target.name }),
      description: t("The venue disappears from the app and can't take new bookings. The owner is notified. The provider's other venues aren't affected."),
      confirmLabel: t("Take down"),
      fieldLabel: t("Reason (shown to the owner and kept in the audit log)"),
      minLength: 10,
      tone: "danger",
    });
    if (reason) await run(() => consoleApi.takeDownVenue(target.id, reason), t("Venue taken down."), t("The venue was not taken down."));
  };

  const restore = async (target: ConsoleVenueDetail) => {
    const reason = await ask({
      title: t("Put {name} back on gaamed?", { name: target.name }),
      description: t("The venue becomes live again if its provider is approved and it has a bookable unit."),
      confirmLabel: t("Restore venue"),
      fieldLabel: t("Why it's safe to restore"),
      minLength: 10,
    });
    if (reason) await run(() => consoleApi.restoreVenue(target.id, reason), t("Venue restored."), t("The venue was not restored."));
  };

  const decisionOpen = detail && ["pending", "removal_requested", "rejected"].includes(detail.approval_status);

  return (
    <div className="console-page">
      {dialog}
      <LoadState error={venue.error} isEmpty={!detail} loading={venue.loading} onRetry={() => void venue.reload()}>
        {detail ? (
          <>
            <PageHeader
              actions={
                isAdmin ? (
                  detail.approval_status === "removed" ? (
                    <button className="ops-button" data-size="sm" data-variant="secondary" disabled={busy} onClick={() => void restore(detail)} type="button">{t("Restore venue")}</button>
                  ) : ["approved", "removal_requested"].includes(detail.approval_status) ? (
                    <button className="ops-button" data-size="sm" data-variant="danger" disabled={busy} onClick={() => void takeDown(detail)} type="button">{t("Take down")}</button>
                  ) : null
                ) : null
              }
              description={
                <>
                  <StatusChip status={detail.approval_status} />{" "}
                  <span className="ops-chip" data-tone={detail.is_publicly_visible ? "success" : "warning"}>
                    {detail.is_publicly_visible ? t("Visible in the app") : t("Not visible in the app")}
                  </span>
                </>
              }
              kicker={<Link className="console-link" to={`/admin/providers/${detail.provider_id}`}>{detail.provider_name}</Link>}
              title={detail.name}
            />
            {banners}
            <div className="console-record-grid">
              <div className="console-record-main">
                <Section description={t("What the app checks before showing this venue to players.")} title={t("Why is it (not) visible?")}>
                  <ul className="console-checklist">
                    {Object.entries(detail.visibility_checks).map(([label, passed]) => (
                      <li data-passed={passed ? "true" : "false"} key={label}>
                        <span aria-hidden="true">{passed ? "✓" : "✕"}</span>
                        <span>{t(label)}</span>
                        <span className="sr-only">{passed ? t("passed") : t("missing")}</span>
                      </li>
                    ))}
                  </ul>
                </Section>

                {decisionOpen ? (
                  <Section title={detail.approval_status === "removal_requested" ? t("Removal request") : t("Decision")}>
                    {detail.submission_note ? <p className="console-quote">{t("Owner's note: {note}", { note: detail.submission_note })}</p> : null}
                    {detail.removal_note ? <p className="console-quote">{t("Removal reason: {note}", { note: detail.removal_note })}</p> : null}
                    <label className="ops-field">
                      <span className="ops-label">{t("Note to the owner")}</span>
                      <textarea className="ops-textarea" onChange={(event) => setNote(event.target.value)} rows={2} value={note} />
                    </label>
                    <div className="console-button-row">
                      <button className="ops-button" data-size="sm" data-variant="primary" disabled={busy} onClick={() => void decide("approve")} type="button">
                        {detail.approval_status === "removal_requested" ? t("Approve removal") : t("Approve")}
                      </button>
                      {detail.approval_status !== "rejected" ? (
                        <button className="ops-button" data-size="sm" data-variant="danger" disabled={busy} onClick={() => void decide("reject")} type="button">
                          {detail.approval_status === "removal_requested" ? t("Keep the venue") : t("Reject")}
                        </button>
                      ) : null}
                    </div>
                  </Section>
                ) : null}

                <Section title={t("Summary")}>
                  <Facts
                    items={[
                      [t("Address"), detail.address],
                      [t("City"), detail.city],
                      [t("Timezone"), detail.timezone],
                      [t("Map pin"), detail.latitude !== null && detail.longitude !== null ? `${detail.latitude.toFixed(5)}, ${detail.longitude.toFixed(5)}` : t("Missing")],
                      [t("Provider status"), <StatusChip key="provider" status={detail.provider_status} />],
                      [t("Added"), dateOnly(detail.created_at)],
                      [t("Last decision"), detail.reviewed_at ? dateTime(detail.reviewed_at) : null],
                      [t("Rating"), detail.average_stars !== null ? t("{stars} ★ from {count} published reviews", { stars: detail.average_stars, count: detail.review_counts.approved ?? 0 }) : t("No published reviews")],
                    ]}
                  />
                  {detail.review_note ? <p className="console-quote">{t("Note to the owner: {note}", { note: detail.review_note })}</p> : null}
                </Section>

                <Section title={t("Units")}>
                  {detail.units.length === 0 ? (
                    <p className="console-muted">{t("No units yet.")}</p>
                  ) : (
                    <div className="console-table-wrap">
                      <table className="console-table">
                        <thead>
                          <tr>
                            <th scope="col">{t("Unit")}</th>
                            <th scope="col">{t("Type")}</th>
                            <th scope="col">{t("Status")}</th>
                            <th scope="col">{t("Hourly rate")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.units.map((unit) => (
                            <tr key={unit.id}>
                              <td>{unit.name}{unit.is_vip ? ` · ${t("VIP")}` : ""}</td>
                              <td>{t(unit.type)}</td>
                              <td>{t(unit.status)}</td>
                              <td>{money(unit.base_hourly_rate)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Section>

                <Section title={t("Opening hours")}>
                  <ul className="console-hours">
                    {detail.operating_hours.map((hour) => (
                      <li key={hour.day_of_week}>
                        <span>{t(DAYS[hour.day_of_week] ?? String(hour.day_of_week))}</span>
                        <span>{hour.is_open ? `${hour.open_time ?? "?"}–${hour.close_time ?? "?"}` : t("Closed")}</span>
                      </li>
                    ))}
                  </ul>
                  {detail.upcoming_blackouts.length > 0 ? (
                    <>
                      <p className="ops-label">{t("Upcoming closures")}</p>
                      <ul className="console-list">
                        {detail.upcoming_blackouts.map((blackout) => (
                          <li className="console-muted" key={blackout.id}>
                            {dateTime(blackout.start_at)} → {dateTime(blackout.end_at)}{blackout.note ? ` · ${blackout.note}` : ""}
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : null}
                </Section>

                <VenueReviews isAdmin={isAdmin} venueId={detail.id} />
                <ActivityTimeline filters={{ venue_id: detail.id }} />
              </div>

              <div className="console-record-side">
                <CommissionRules isAdmin={isAdmin} onChanged={() => void venue.reload()} rules={detail.commission_rules} venueId={detail.id} />
                <NotesPanel entityId={detail.id} entityType="venue" />
              </div>
            </div>
          </>
        ) : null}
      </LoadState>
    </div>
  );
}

const BASE_OPTIONS: Array<{ value: CommissionBase; label: string; help: string }> = [
  { value: "session_time", label: "Session time only", help: "What the venue charges for play time" },
  { value: "food", label: "Food and drinks only", help: "What the venue charges for food orders" },
  { value: "total", label: "Session time and food", help: "Both together" },
];

export function CommissionRules({
  isAdmin,
  onChanged,
  rules,
  venueId,
}: {
  isAdmin: boolean;
  onChanged: () => void;
  rules: ConsoleCommissionRule[];
  venueId: string;
}) {
  const { t } = useAppPreferences();
  const { dateOnly } = useFormatters();
  const today = new Date().toISOString().slice(0, 10);
  const [base, setBase] = useState<CommissionBase>("session_time");
  const [percent, setPercent] = useState("");
  const [start, setStart] = useState(today);
  const [end, setEnd] = useState("");
  const [ruleNote, setRuleNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [endDates, setEndDates] = useState<Record<string, string>>({});
  const current = rules.filter((rule) => rule.is_current);

  const add = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await consoleApi.createCommissionRule(venueId, {
        base,
        percent: Number(percent),
        effective_from: start,
        effective_to: end || null,
        note: ruleNote.trim() || null,
      });
      setPercent("");
      setRuleNote("");
      setEnd("");
      onChanged();
    } catch (saveError) {
      setError(errorMessage(saveError, t("The rule was not saved.")));
    } finally {
      setSaving(false);
    }
  };

  const endRule = async (rule: ConsoleCommissionRule) => {
    const date = endDates[rule.id] || today;
    setError(null);
    try {
      await consoleApi.endCommissionRule(rule.id, date);
      onChanged();
    } catch (endError) {
      setError(errorMessage(endError, t("The rule was not ended.")));
    }
  };

  return (
    <Section
      description={t("What gaamed takes from this venue. Combine rules, e.g. 15% of session time plus 5% of food.")}
      title={t("Commission terms")}
    >
      {current.length === 0 ? (
        <p className="console-warning">{t("No terms today. Sessions here earn gaamed nothing.")}</p>
      ) : (
        <ul className="console-rule-summary">
          {current.map((rule) => (
            <li key={rule.id}>
              <strong>{rule.percent}%</strong> {t("of {base}", { base: baseLabel(rule.base, t) })}
            </li>
          ))}
        </ul>
      )}
      {rules.length > 0 ? (
        <ul className="console-list">
          {rules.map((rule) => (
            <li className="console-rule" data-current={rule.is_current ? "true" : undefined} key={rule.id}>
              <div className="min-w-0 flex-1">
                <p className="console-list-title">
                  {rule.percent}% · {baseLabel(rule.base, t)}
                </p>
                <p className="console-muted">
                  {dateOnly(rule.effective_from)} → {rule.effective_to ? dateOnly(rule.effective_to) : t("no end date")}
                  {rule.created_by_name ? ` · ${rule.created_by_name}` : ""}
                </p>
                {rule.note ? <p className="console-muted">{rule.note}</p> : null}
              </div>
              {isAdmin && (!rule.effective_to || rule.effective_to > today) ? (
                <div className="console-rule-end">
                  <label className="sr-only" htmlFor={`end-${rule.id}`}>{t("End date")}</label>
                  <input
                    className="ops-input"
                    id={`end-${rule.id}`}
                    min={rule.effective_from}
                    onChange={(event) => setEndDates((currentDates) => ({ ...currentDates, [rule.id]: event.target.value }))}
                    type="date"
                    value={endDates[rule.id] ?? today}
                  />
                  <button className="ops-button" data-size="sm" data-variant="secondary" onClick={() => void endRule(rule)} type="button">
                    {t("End rule")}
                  </button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {error ? <p className="console-error" role="alert">{error}</p> : null}
      {isAdmin ? (
        <form className="console-form" onSubmit={(event) => void add(event)}>
          <div className="ops-field console-form-wide">
            <label className="ops-label" htmlFor={`base-${venueId}`}>{t("Charged on")}</label>
            <select
              aria-describedby={`base-help-${venueId}`}
              className="ops-select"
              id={`base-${venueId}`}
              onChange={(event) => setBase(event.target.value as CommissionBase)}
              value={base}
            >
              {BASE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{t(option.label)}</option>
              ))}
            </select>
            <span className="console-muted" id={`base-help-${venueId}`}>{t(BASE_OPTIONS.find((option) => option.value === base)?.help ?? "")}</span>
          </div>
          <label className="ops-field">
            <span className="ops-label">{t("Percent")}</span>
            <input className="ops-input" inputMode="decimal" max={100} min={0.01} onChange={(event) => setPercent(event.target.value)} required step={0.01} type="number" value={percent} />
          </label>
          <label className="ops-field">
            <span className="ops-label">{t("Starts")}</span>
            <input className="ops-input" onChange={(event) => setStart(event.target.value)} required type="date" value={start} />
          </label>
          <label className="ops-field">
            <span className="ops-label">{t("Ends (optional)")}</span>
            <input className="ops-input" min={start} onChange={(event) => setEnd(event.target.value)} type="date" value={end} />
          </label>
          <label className="ops-field console-form-wide">
            <span className="ops-label">{t("Note (optional)")}</span>
            <input className="ops-input" maxLength={500} onChange={(event) => setRuleNote(event.target.value)} placeholder={t("e.g. launch discount agreed with the owner")} value={ruleNote} />
          </label>
          <div className="console-form-wide">
            <button className="ops-button" data-size="sm" data-variant="primary" disabled={saving || !percent} type="submit">
              {saving ? t("Saving...") : t("Add rule")}
            </button>
          </div>
        </form>
      ) : (
        <p className="console-muted">{t("Only admins can change commission terms.")}</p>
      )}
    </Section>
  );
}

function VenueReviews({ isAdmin, venueId }: { isAdmin: boolean; venueId: string }) {
  const { t } = useAppPreferences();
  const { dateOnly } = useFormatters();
  const { ask, dialog } = useReasonPrompt();
  const reviews = useLoad(() => consoleApi.listReviews({ venue_id: venueId }).then((result) => result.reviews), [venueId]);
  const [error, setError] = useState<string | null>(null);

  const remove = async (reviewId: string) => {
    const rationale = await ask({
      title: t("Remove this published review?"),
      description: t("It stops showing on the venue page. The author isn't notified."),
      confirmLabel: t("Remove review"),
      fieldLabel: t("Why it breaks the review rules"),
      minLength: 10,
      tone: "danger",
    });
    if (!rationale) return;
    setError(null);
    try {
      await consoleApi.removeReview(reviewId, rationale);
      await reviews.reload();
    } catch (removeError) {
      setError(errorMessage(removeError, t("The review was not removed.")));
    }
  };

  return (
    <Section title={t("Reviews")}>
      {dialog}
      {error ? <p className="console-error" role="alert">{error}</p> : null}
      <LoadState empty={t("No reviews yet.")} error={reviews.error} isEmpty={(reviews.data ?? []).length === 0} loading={reviews.loading} onRetry={() => void reviews.reload()}>
        <ul className="console-list">
          {(reviews.data ?? []).map((review) => (
            <li className="console-review" key={review.id}>
              <div className="min-w-0 flex-1">
                <p className="console-list-title">
                  {"★".repeat(review.stars)}{"☆".repeat(5 - review.stars)} · {review.customer_user_id ? (
                    <Link className="console-link" to={`/admin/accounts/${review.customer_user_id}`}>{review.customer_name ?? t("Player")}</Link>
                  ) : t("Deleted account")}
                </p>
                <p className="console-quote">{review.comment || t("No comment")}</p>
                <p className="console-muted">{dateOnly(review.created_at)}{review.review_note ? ` · ${review.review_note}` : ""}</p>
              </div>
              <StatusChip status={review.status} />
              {isAdmin && review.status === "approved" ? (
                <button className="ops-button" data-size="sm" data-variant="secondary" onClick={() => void remove(review.id)} type="button">{t("Remove")}</button>
              ) : null}
            </li>
          ))}
        </ul>
      </LoadState>
    </Section>
  );
}
